// Lit windows.
//
// A blind critic named these as THE single largest tell twice running:
//
//   "Every one, in all five images, is a flat uniform cream rectangle: no interior content, no
//    vertical luminance falloff, no blown-out core, no per-window colour-temperature variation.
//    Real night windows are the most information-dense thing in a street photo -- you see ceilings,
//    blinds, furniture silhouettes, and every window is a slightly different colour."
//
// The fix cannot be a baked tile: a tiled texture would repeat the SAME interior in every window,
// which reads as clones rather than as a building full of people. Each pane needs its own identity.
//
// So each pane carries a per-instance attribute `aPane = (hash, lit, temp)`, set at build time, and
// the shader synthesises the interior from it in pane-local UV space. BoxGeometry already gives
// every face a 0..1 UV with V running vertically on the large faces of a thin pane, so no extra UV
// work is needed.

import * as THREE from 'three';
import { hashFloat } from '../rng.js';

const WINDOW_GLSL = /* glsl */`
varying vec3 vPane;
varying vec2 vPaneUV;

float wn_hash(float x){ return fract(sin(x * 127.1) * 43758.5453123); }
`;

export function makeWindowMaterial() {
  const mat = new THREE.MeshStandardMaterial({
    color: 0x090c11,
    roughness: 0.12,
    metalness: 0.10,
    emissive: 0xffffff,
    emissiveIntensity: 1.0,
  });

  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>',
        '#include <common>\nattribute vec3 aPane;\n' + WINDOW_GLSL)
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvPane = aPane;\nvPaneUV = uv;');

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + WINDOW_GLSL)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        float wH = vPane.x;
        float wLit = vPane.y;
        float wTemp = vPane.z;
        vec2 wp = clamp(vPaneUV, 0.0, 1.0);

        // Vertical luminance profile. A lit room is brightest at the ceiling and falls off to a
        // dark sill -- that gradient alone is most of what separates a window from a light panel.
        float prof = 0.16 + 0.84 * smoothstep(0.02, 0.55, 1.0 - wp.y);
        prof *= smoothstep(0.0, 0.09, wp.y);          // dark band at the sill
        prof *= smoothstep(0.0, 0.05, 1.0 - wp.y);    // head shadow under the lintel

        // A hot core near the fitting, which is what clips in a real photograph.
        // TIGHT core. Round 4 removed the clipping entirely and the critic's verdict flipped from
        // "featureless slab" to "no key and no tonal range: nothing clips white, nothing reaches
        // black, all five frames sit in a compressed midtone band no phone sensor would produce".
        // Both are right. A real practical has a SMALL blown centre and a large unclipped body --
        // so the core radius drops from 0.34 to 0.13 and its gain goes up, which buys back the
        // highlight without flattening the pane.
        float core = smoothstep(0.13, 0.0, distance(wp, vec2(0.30 + 0.42 * wn_hash(wH * 3.7), 0.86)));
        // Round 4: this hot core was pushing panes hard into clipping, and a clipped pane is a
        // featureless slab -- which is exactly what a critic named as the largest tell: "there is
        // luminance but no source; real wet-night photography never has an emitter without
        // content; you always see WHAT is lit." Pulled ~1.5 stops so the interior survives.
        prof += core * 2.6;

        // Occluders: a horizontal blind or curtain head, a vertical mullion or curtain edge, and a
        // furniture silhouette sitting on the sill. Positions hashed per pane so no two match.
        float barY = 0.34 + 0.46 * wn_hash(wH * 11.3);
        float bar = 1.0 - 0.72 * smoothstep(0.055, 0.0, abs(wp.y - barY));
        float mulX = 0.22 + 0.56 * wn_hash(wH * 5.1);
        float mul = 1.0 - 0.55 * smoothstep(0.030, 0.0, abs(wp.x - mulX));

        // Roughly one window in four has slatted blinds down.
        float blindOn = step(0.74, wn_hash(wH * 19.7));
        float slats = 0.40 + 0.60 * step(0.45, fract(wp.y * 15.0));
        float blind = mix(1.0, slats, blindOn);

        // Furniture / a body near the glass.
        vec2 fp = vec2(0.20 + 0.60 * wn_hash(wH * 23.1), 0.12);
        float furn = 1.0 - 0.80 * smoothstep(0.17, 0.02, distance(wp * vec2(1.0, 1.7), fp * vec2(1.0, 1.7)));

        float I = max(prof, 0.0) * bar * mul * blind * furn;

        // Colour temperature per pane: sodium/tungsten through fluorescent to screen-blue. A street
        // where every window is the same cream is the giveaway that they came from one material.
        vec3 tungsten = vec3(1.00, 0.63, 0.31);
        vec3 fluoro   = vec3(0.86, 0.98, 0.86);
        vec3 screen   = vec3(0.42, 0.66, 1.00);
        vec3 col = wTemp < 0.5
          ? mix(tungsten, fluoro, wTemp * 2.0)
          : mix(fluoro, screen, (wTemp - 0.5) * 2.0);

        // Per-pane brightness spread -- rooms are not all lit to the same level.
        float gain = 0.42 + 0.95 * wn_hash(wH * 7.9);

        totalEmissiveRadiance = col * I * gain * wLit * 1.55;

        // Unlit panes are not black: they hold a dim skylit interior so the opening still reads.
        totalEmissiveRadiance += vec3(0.020, 0.026, 0.038) * (1.0 - wLit) * (0.35 + 0.65 * prof);`);
  };

  mat.customProgramCacheKey = () => 'np-window-v1';
  return mat;
}

/**
 * Per-pane attribute values. `lit` and `temp` are decided here rather than by the caller so every
 * window in the city is drawn from the same distribution.
 */
export function paneAttr(key) {
  const h = hashFloat(`pane.${key}`);
  const lit = hashFloat(`pane.lit.${key}`) < 0.42 ? 1 : 0;
  // Warm-biased: most domestic and retail interiors are tungsten or warm LED, with a minority of
  // cold fluorescent and the occasional screen-blue room.
  const t = hashFloat(`pane.temp.${key}`);
  const temp = t < 0.46 ? t * 0.30 : t < 0.80 ? 0.34 + (t - 0.46) * 1.5 : 0.86 + (t - 0.80) * 0.7;
  return [h, lit, Math.min(1, temp)];
}
